import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import InvoiceCreateDialog from "../../components/admin/InvoiceCreateDialog";
import InvoiceDueReminderDialog from "../../components/admin/InvoiceDueReminderDialog";
import InvoiceEditSheet from "../../components/admin/InvoiceEditSheet";
import InvoiceRowActions from "../../components/admin/InvoiceRowActions";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import {
  CHART_MONTH_SLOT_PX,
  defaultRevenueYear,
  filterInvoicesByYear,
  formatDisplayDate,
  formatUsd,
  formatUsdCompact,
  getAvailableYears,
  getMonthlyCollectedSeries,
  getRevenueMetrics,
  invoiceCollected,
  invoiceIssuedYear,
  invoiceOutstanding,
  invoicePaidAt,
  matchesStatusFilter,
} from "../../data/revenueData";
import { fetchRevenueInvoices, patchRevenueInvoice } from "../../lib/revenueApi";
import { formatApiDetail } from "../../lib/api";
import { cn } from "../../lib/utils";
import { MotionSection } from "../../components/Motion";

const STATUS_FILTERS = [
  ["all", "All", "directory"],
  ["DRAFT", "Draft", "unsent"],
  ["DUE", "Due", "this cycle"],
  ["OUTSTANDING", "Open", "unpaid"],
  ["OVERDUE", "Overdue", "late"],
  ["PAID", "Paid", "collected"],
];

const STATUS_ACCENT = {
  all: "#9AA3B2",
  DRAFT: "#9AA3B2",
  DUE: "#001EFF",
  OUTSTANDING: "#2E4060",
  OVERDUE: "#D2271F",
  PAID: "#001EFF",
  PARTIAL: "#001EFF",
};

const CHART_TICK = {
  fill: "#888888",
  fontSize: 10,
  fontFamily: "'Nohemi', sans-serif",
  fontWeight: 300,
};

const CHART_GRID_STROKE = "#E2E5E9";
const CHART_AXIS_STROKE = "#C8CCD2";
/** Matches YAxis `width` — used to center the current month in the scroll view. */
const CHART_Y_AXIS_WIDTH = 56;

const INVOICE_STATUS_MENU = [
  { value: "ISSUED", label: "Mark issued" },
  { value: "PAID", label: "Mark as paid" },
];

function useChartHeight() {
  const [height, setHeight] = useState(360);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const update = () => {
      const w = window.innerWidth;
      if (w < 640) setHeight(320);
      else if (w < 1024) setHeight(380);
      else setHeight(440);
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return height;
}

function MonthlyChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const value = Number(payload[0]?.value) || 0;
  return (
    <div className="neo-panel border border-[var(--t-border)] px-3 py-2 shadow-none">
      <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--t-meta)]">{label}</div>
      <div className="mt-0.5 font-heading text-sm tracking-tight !font-extralight tabular-nums text-[var(--t-text)]">
        {formatUsd(value)}
      </div>
    </div>
  );
}

function MonthlyCollectedBarChart({ data, width, height, animate = false }) {
  return (
    <BarChart
      width={width}
      height={height}
      data={data}
      margin={{ top: 36, right: 20, left: 0, bottom: 28 }}
      barCategoryGap="12%"
      barGap={2}
    >
      <CartesianGrid stroke={CHART_GRID_STROKE} strokeWidth={1} vertical horizontal />
      <XAxis
        dataKey="label"
        axisLine={{ stroke: CHART_AXIS_STROKE }}
        tickLine={{ stroke: CHART_AXIS_STROKE }}
        tick={CHART_TICK}
        interval={0}
        textAnchor="middle"
        height={40}
      />
      <YAxis
        axisLine={{ stroke: CHART_AXIS_STROKE }}
        tickLine={{ stroke: CHART_AXIS_STROKE }}
        tick={CHART_TICK}
        tickFormatter={(v) => formatUsdCompact(v)}
        width={CHART_Y_AXIS_WIDTH}
        tickCount={5}
        domain={[0, (dataMax) => Math.max(dataMax * 1.15, 1)]}
      />
      <Tooltip
        content={<MonthlyChartTooltip />}
        cursor={{ fill: "rgba(0, 30, 255, 0.04)" }}
        isAnimationActive={false}
      />
      <Bar
        dataKey="total"
        fill="#001EFF"
        radius={[4, 4, 0, 0]}
        maxBarSize={48}
        isAnimationActive={animate}
        animationBegin={40}
        animationDuration={900}
        animationEasing="ease-out"
      >
        <LabelList
          dataKey="total"
          position="top"
          formatter={(v) => (v > 0 ? formatUsdCompact(v) : "")}
          isAnimationActive={animate}
          style={{
            fill: "#222222",
            fontSize: 10,
            fontFamily: "'Nohemi', sans-serif",
            fontWeight: 300,
          }}
        />
      </Bar>
    </BarChart>
  );
}

export default function RevenuePage() {
  const reduce = useReducedMotion();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [editInvoice, setEditInvoice] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [reminderInvoice, setReminderInvoice] = useState(null);
  const [reminderOpen, setReminderOpen] = useState(false);

  const availableYears = useMemo(() => getAvailableYears(invoices), [invoices]);
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [yearOpen, setYearOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [invoiceLimit, setInvoiceLimit] = useState(5);
  const chartHeight = useChartHeight();
  const chartSectionRef = useRef(null);
  const chartScrollRef = useRef(null);
  const chartInView = useInView(chartSectionRef, {
    once: true,
    amount: 0.2,
    margin: "0px 0px -8% 0px",
  });
  // Mount chart only after it enters view so Recharts can play the bar grow once.
  const [chartMounted, setChartMounted] = useState(false);
  useEffect(() => {
    if (!chartInView || chartMounted) return undefined;
    const delay = reduce ? 0 : 160;
    const t = window.setTimeout(() => setChartMounted(true), delay);
    return () => window.clearTimeout(t);
  }, [chartInView, chartMounted, reduce]);
  // Fallback if IntersectionObserver never fires (e.g. odd overflow roots).
  useEffect(() => {
    if (chartMounted) return undefined;
    const t = window.setTimeout(() => setChartMounted(true), 1800);
    return () => window.clearTimeout(t);
  }, [chartMounted]);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const rows = await fetchRevenueInvoices();
      setInvoices(rows);
      setSelectedYear((prev) => {
        const years = getAvailableYears(rows);
        if (years.includes(prev)) return prev;
        return defaultRevenueYear(rows);
      });
    } catch (e) {
      const status = e.response?.status;
      const base = formatApiDetail(e.response?.data?.detail, "Could not load revenue data.");
      setLoadError(status ? `${base} (HTTP ${status})` : base);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const handleInvoiceSaved = (updated) => {
    if (!updated) return;
    setInvoices((prev) =>
      prev.map((row) => {
        if (updated.id && row.id === updated.id) return updated;
        if (updated.invoiceNumber && row.invoiceNumber === updated.invoiceNumber) return updated;
        return row;
      }),
    );
    setEditInvoice((prev) => {
      if (!prev) return prev;
      if (updated.id && prev.id === updated.id) return updated;
      if (updated.invoiceNumber && prev.invoiceNumber === updated.invoiceNumber) return updated;
      return prev;
    });
  };

  const handleInvoiceDeleted = (deleted) => {
    if (!deleted) return;
    setInvoices((prev) =>
      prev.filter((row) => {
        if (deleted.id && row.id === deleted.id) return false;
        if (deleted.invoiceNumber && row.invoiceNumber === deleted.invoiceNumber) return false;
        return true;
      }),
    );
    setEditInvoice(null);
  };

  const handleInvoiceCreated = (created) => {
    if (!created) return;
    setInvoices((prev) => [created, ...prev]);
    const year = invoiceIssuedYear(created);
    if (year) setSelectedYear(year);
    setStatusFilter("all");
    toast.success(`Invoice ${created.invoiceNumber} added`);
  };

  const handleStatusChange = async (row, action) => {
    const invoiceKey = row?.id || row?.invoiceNumber;
    if (!invoiceKey || !action) return false;
    const isDraft = Boolean(row.isDraft) || row.status === "DRAFT";
    const isPaid = (row.status || "").toUpperCase() === "PAID";
    let body = null;
    let successMsg = "";
    if (action === "ISSUED") {
      if (!isDraft) return false;
      body = { is_draft: false };
      successMsg = "Invoice marked as issued";
    } else if (action === "PAID") {
      if (isPaid) return false;
      body = { status: "PAID", is_draft: false };
      successMsg = "Invoice marked as paid";
    } else {
      return false;
    }
    try {
      const data = await patchRevenueInvoice(invoiceKey, body);
      handleInvoiceSaved(data?.invoice);
      toast.success(successMsg);
      if (data?.promotion?.client_id) {
        toast.success("Prospect promoted to active engagement");
      }
      return true;
    } catch (e) {
      toast.error(formatApiDetail(e.response?.data?.detail, "Could not update status."));
      return false;
    }
  };

  const openEdit = (row) => {
    setEditInvoice(row);
    setEditOpen(true);
  };

  const openReminder = (row) => {
    setReminderInvoice(row);
    setReminderOpen(true);
  };

  const yearInvoices = useMemo(
    () => filterInvoicesByYear(invoices, selectedYear),
    [invoices, selectedYear],
  );

  const metrics = useMemo(() => getRevenueMetrics(invoices, selectedYear), [invoices, selectedYear]);
  const monthlySeries = useMemo(
    () => getMonthlyCollectedSeries(invoices, selectedYear),
    [invoices, selectedYear],
  );
  const showMonthlyChart = monthlySeries.some((m) => (Number(m.total) || 0) > 0);
  const chartScrollWidth = Math.max(monthlySeries.length * CHART_MONTH_SLOT_PX, 640);

  useEffect(() => {
    setInvoiceLimit(5);
  }, [selectedYear, statusFilter]);

  useEffect(() => {
    if (!showMonthlyChart || !chartMounted) return undefined;
    const el = chartScrollRef.current;
    if (!el) return undefined;

    const focusCurrentMonth = () => {
      const now = new Date();
      if (selectedYear !== now.getFullYear()) {
        el.scrollTo({ left: 0, behavior: "auto" });
        return;
      }
      const monthIndex = now.getMonth();
      const plotWidth = Math.max(el.scrollWidth - CHART_Y_AXIS_WIDTH, CHART_MONTH_SLOT_PX);
      const slot = plotWidth / Math.max(monthlySeries.length, 1);
      const target = Math.max(
        0,
        CHART_Y_AXIS_WIDTH + monthIndex * slot + slot / 2 - el.clientWidth / 2,
      );
      el.scrollTo({ left: target, behavior: reduce ? "auto" : "smooth" });
    };

    const id = window.requestAnimationFrame(focusCurrentMonth);
    window.addEventListener("resize", focusCurrentMonth);
    return () => {
      window.cancelAnimationFrame(id);
      window.removeEventListener("resize", focusCurrentMonth);
    };
  }, [showMonthlyChart, chartMounted, selectedYear, monthlySeries.length, chartScrollWidth, reduce]);

  const statusCounts = useMemo(() => {
    const counts = { all: yearInvoices.length };
    STATUS_FILTERS.forEach(([key]) => {
      if (key === "all") return;
      counts[key] = yearInvoices.filter((row) => matchesStatusFilter(row, key)).length;
    });
    return counts;
  }, [yearInvoices]);

  const statusMix = useMemo(
    () =>
      STATUS_FILTERS.filter(([key]) => key !== "all").map(([key, label]) => ({
        key,
        label,
        count: statusCounts[key] || 0,
        accent: STATUS_ACCENT[key],
      })),
    [statusCounts],
  );

  const filteredInvoices = useMemo(() => {
    return [...yearInvoices]
      .filter((row) => matchesStatusFilter(row, statusFilter))
      .sort((a, b) => String(b.issued || "").localeCompare(String(a.issued || "")));
  }, [yearInvoices, statusFilter]);

  const visibleInvoices = filteredInvoices.slice(0, invoiceLimit);
  const hasMoreInvoices = filteredInvoices.length > invoiceLimit;

  return (
    <div className="min-h-0 max-w-full overflow-x-clip p-4 sm:p-5 md:p-7" data-testid="revenue-page">
      <MotionSection
        as="header"
        className="mb-5 flex flex-row items-center justify-between gap-3 sm:mb-6"
      >
        <div className="label-eyebrow mb-0">Revenue</div>
        <RevenueYearSelect
          years={availableYears.length ? availableYears : [selectedYear]}
          selectedYear={selectedYear}
          onSelect={setSelectedYear}
          open={yearOpen}
          onOpenChange={setYearOpen}
        />
      </MotionSection>

      {loadError ? (
        <p className="mb-4 text-xs text-[var(--t-error)]">{loadError}</p>
      ) : null}

      <InvoiceCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={handleInvoiceCreated}
      />

      <InvoiceEditSheet
        invoice={editInvoice}
        open={editOpen}
        onOpenChange={setEditOpen}
        onSaved={handleInvoiceSaved}
        onDeleted={handleInvoiceDeleted}
      />

      <InvoiceDueReminderDialog
        invoice={reminderInvoice}
        open={reminderOpen}
        onOpenChange={setReminderOpen}
      />

      {loading ? (
        <p className="py-16 text-center text-sm text-[var(--t-meta)]">Loading…</p>
      ) : (
        <>
          <MotionSection className="mb-5 grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
            <section className="neo-panel p-5" data-testid="revenue-metrics">
              <div className="label-eyebrow mb-3">Cash pulse</div>
              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                {[
                  ["Collected", formatUsd(metrics.totalCollected)],
                  ["Outstanding", formatUsd(metrics.outstanding)],
                  ["MRR", formatUsd(metrics.mrr)],
                ].map(([label, value], idx) => (
                  <motion.div
                    key={label}
                    initial={reduce ? false : { opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: idx * 0.06 }}
                  >
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--t-meta)]">{label}</div>
                    <div className="mt-1 font-heading text-lg tracking-tight !font-extralight tabular-nums sm:text-2xl">
                      {value}
                    </div>
                  </motion.div>
                ))}
              </div>
            </section>

            <section className="neo-panel p-5" data-testid="revenue-status-mix">
              <div className="label-eyebrow mb-3">Status mix</div>
              <div className="space-y-2.5">
                {statusMix.map((row, idx) => {
                  const pct = statusCounts.all ? Math.round((row.count / statusCounts.all) * 100) : 0;
                  const width = row.count === 0 ? 0 : Math.max(pct, 6);
                  return (
                    <button
                      key={row.key}
                      type="button"
                      onClick={() => setStatusFilter(row.key)}
                      className="block w-full text-left"
                      data-testid={`revenue-mix-${row.key}`}
                    >
                      <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                        <span
                          className={
                            statusFilter === row.key ? "text-[var(--t-accent)]" : "text-[var(--t-meta)]"
                          }
                        >
                          {row.label}
                        </span>
                        <span className="tabular-nums text-[var(--t-text)]">{row.count}</span>
                      </div>
                      <div className="neo-well h-2 overflow-hidden rounded-full">
                        <motion.div
                          className="h-full rounded-full"
                          style={{ background: row.accent }}
                          initial={reduce ? false : { width: 0 }}
                          animate={{ width: `${width}%` }}
                          transition={{ duration: 0.55, delay: idx * 0.05 }}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          </MotionSection>

          <MotionSection
            className="grid grid-cols-1 gap-4 lg:grid-cols-[14rem_minmax(0,1fr)] lg:items-start"
            data-testid="invoices-table"
          >
            <div className="flex flex-col gap-3 lg:sticky lg:top-4">
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                disabled={loading}
                className="neo-cta w-full justify-center disabled:opacity-50"
                data-testid="revenue-add-invoice"
              >
                <Plus size={16} strokeWidth={1.4} /> Invoice
              </button>
              <aside className="hidden neo-panel p-2 lg:block" data-testid="invoice-status-tabs">
                <div className="space-y-1">
                  {STATUS_FILTERS.map(([key, label, hint]) => {
                    const on = statusFilter === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setStatusFilter(key)}
                        className={`neo-nav-item w-[calc(100%-1.5rem)] ${on ? "is-active" : ""}`}
                        data-testid={`invoice-tab-${key}`}
                      >
                        <span className="flex-1 text-left">{label}</span>
                        <span className="tabular-nums text-[10px] opacity-70">{statusCounts[key] || 0}</span>
                        <span className="sr-only">{hint}</span>
                      </button>
                    );
                  })}
                </div>
              </aside>
            </div>

            <div className="min-w-0 space-y-3">
              <div className="flex items-baseline justify-between gap-3 px-0.5">
                <div className="label-eyebrow mb-0">Invoices</div>
                <span className="text-xs tabular-nums text-[var(--t-meta)]">{filteredInvoices.length}</span>
              </div>

              {filteredInvoices.length === 0 ? (
                <section
                  className="neo-panel p-8 text-center text-sm text-[var(--t-meta)]"
                  data-testid="invoices-empty"
                >
                  No invoices in this status.
                </section>
              ) : (
                <div className="space-y-2.5">
                  {visibleInvoices.map((row, idx) => (
                    <InvoiceRailCard
                      key={row.id || row.invoiceNumber}
                      row={row}
                      onEdit={openEdit}
                      onSendReminder={openReminder}
                      onStatusChange={handleStatusChange}
                      delay={idx * 0.03}
                      reduce={reduce}
                    />
                  ))}
                  {hasMoreInvoices ? (
                    <button
                      type="button"
                      onClick={() => setInvoiceLimit((n) => n + 5)}
                      className="neo-cta w-full justify-center"
                      data-testid="invoices-next-5"
                    >
                      Next 5
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          </MotionSection>

          <MotionSection className="neo-panel mt-5 p-5" data-testid="revenue-chart">
            <div ref={chartSectionRef}>
              <div className="label-eyebrow mb-4">Monthly collected</div>
              {showMonthlyChart ? (
                <>
                  <div
                    className="hidden min-h-0 w-full min-w-0 lg:block"
                    style={{ height: chartHeight }}
                    aria-label="Monthly collected revenue"
                  >
                    {chartMounted ? (
                      <ResponsiveContainer width="100%" height="100%" debounce={50}>
                        <MonthlyCollectedBarChart
                          key={`desktop-${selectedYear}`}
                          data={monthlySeries}
                          animate={!reduce}
                        />
                      </ResponsiveContainer>
                    ) : null}
                  </div>
                  <div
                    ref={chartScrollRef}
                    className="w-full min-w-0 overflow-x-auto overflow-y-hidden [-webkit-overflow-scrolling:touch] lg:hidden"
                    style={{ height: chartHeight }}
                    aria-label="Monthly collected revenue — scroll horizontally"
                  >
                    <div
                      className="shrink-0"
                      style={{
                        width: chartScrollWidth,
                        height: chartHeight,
                        minWidth: chartScrollWidth,
                      }}
                    >
                      {chartMounted ? (
                        <MonthlyCollectedBarChart
                          key={`mobile-${selectedYear}`}
                          data={monthlySeries}
                          width={chartScrollWidth}
                          height={chartHeight}
                          animate={!reduce}
                        />
                      ) : null}
                    </div>
                  </div>
                </>
              ) : (
                <p
                  className="py-10 text-center text-sm text-[var(--t-meta)]"
                  data-testid="revenue-chart-empty"
                >
                  No collected revenue in this period yet.
                </p>
              )}
            </div>
          </MotionSection>
        </>
      )}
    </div>
  );
}

function InvoiceRailCard({ row, onEdit, onSendReminder, onStatusChange, delay = 0, reduce }) {
  const collected = invoiceCollected(row);
  const open = invoiceOutstanding(row);
  const accent = STATUS_ACCENT[row.status] || STATUS_ACCENT.OUTSTANDING;
  const isPaid = (row.status || "").toUpperCase() === "PAID";
  const paidAt = isPaid ? invoicePaidAt(row) : null;
  const metaBits = [
    row.invoiceNumber,
    row.type,
    isPaid && paidAt ? `Paid ${formatDisplayDate(paidAt)}` : formatDisplayDate(row.due),
  ].filter(Boolean);

  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}
      className="neo-panel relative overflow-hidden p-4"
      data-testid={`invoice-row-${row.invoiceNumber}`}
    >
      <span
        className="absolute inset-y-3 left-0 w-[3px] rounded-full"
        style={{ background: accent }}
        aria-hidden
      />
      <div className="flex items-start justify-between gap-3 pl-2">
        <button type="button" onClick={() => onEdit?.(row)} className="min-w-0 flex-1 text-left">
          <div className="truncate font-heading text-base tracking-tight !font-light hover:text-[var(--t-accent)]">
            {row.client}
          </div>
          <div className="mt-0.5 text-xs text-[var(--t-meta)]" data-testid={`invoice-meta-${row.invoiceNumber}`}>
            {metaBits.join(" · ")}
          </div>
        </button>
        <div className="flex shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <InvoiceStatusMenu row={row} onStatusChange={onStatusChange} />
          <InvoiceRowActions row={row} onEdit={onEdit} onSendReminder={onSendReminder} />
        </div>
      </div>
      <button
        type="button"
        onClick={() => onEdit?.(row)}
        className="mt-3 grid w-full grid-cols-3 gap-2 pl-2 text-left text-xs"
      >
        <div>
          <div className="text-[10px] uppercase tracking-[0.12em] text-[var(--t-meta)]">Amount</div>
          <div className="mt-0.5 font-heading !font-light tabular-nums">{formatUsd(row.amount)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.12em] text-[var(--t-meta)]">In</div>
          <div className="mt-0.5 font-heading !font-light tabular-nums text-[var(--t-accent)]">
            {formatUsd(collected)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.12em] text-[var(--t-meta)]">Open</div>
          <div className="mt-0.5 font-heading !font-light tabular-nums">{formatUsd(open)}</div>
        </div>
      </button>
    </motion.div>
  );
}

function RevenueYearSelect({ years, selectedYear, onSelect, open, onOpenChange }) {
  const currentYear = new Date().getFullYear();

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-10 min-h-0 max-w-[min(100%,200px)] shrink-0 items-center gap-2 rounded-xl px-3 text-left text-[11px] uppercase tracking-[0.14em] text-[var(--t-meta)] hover:text-[var(--t-text)]"
          data-testid="revenue-year-select"
        >
          <span className="shrink-0">Cycle</span>
          <span
            className={`min-w-0 flex-1 truncate text-sm !normal-case tracking-normal !font-light ${
              selectedYear === currentYear ? "text-[var(--t-accent)]" : "text-[var(--t-text)]"
            }`}
          >
            {selectedYear}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={6}
        className="z-[60] w-auto min-w-[12rem] neo-panel p-2 text-[var(--t-text)] t-glass"
        data-testid="revenue-year-menu"
      >
        <div className="flex max-h-[280px] flex-col gap-1 overflow-auto">
          {years.map((year) => {
            const selected = year === selectedYear;
            return (
              <button
                key={year}
                type="button"
                className={cn(
                  "rounded-[var(--t-event-pill-radius)] px-3 py-2 text-left text-[10px] font-normal uppercase tracking-[0.16em] transition-colors hover:bg-black/[0.04]",
                  selected &&
                    "border border-[var(--t-accent)] bg-[var(--t-accent)]/5 text-[var(--t-accent)]",
                  !selected && year === currentYear && "text-[var(--t-accent)]",
                )}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect(year);
                  onOpenChange(false);
                }}
                data-testid={`revenue-year-option-${year}`}
              >
                {year}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function InvoiceStatusMenu({ row, onStatusChange }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const status = row.status || "OUTSTANDING";
  const isDraft = Boolean(row.isDraft) || status === "DRAFT";
  const isPaid = status === "PAID";
  const menuOptions = isPaid
    ? []
    : isDraft
      ? INVOICE_STATUS_MENU
      : INVOICE_STATUS_MENU.filter((opt) => opt.value === "PAID");

  const selectStatus = async (action) => {
    if (saving) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      const ok = await onStatusChange?.(row, action);
      if (ok) setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={saving || !menuOptions.length}
          className={cn(
            "t-pill t-pill-accent inline-flex shrink-0 border bg-transparent text-left font-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--t-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--t-bg)]",
            menuOptions.length
              ? "cursor-pointer hover:opacity-90 disabled:opacity-60"
              : "cursor-default disabled:opacity-100",
          )}
          data-testid={`invoice-status-trigger-${row.invoiceNumber}`}
          aria-label={`Status for ${row.invoiceNumber}`}
          title={menuOptions.length ? "Update invoice status" : "Status updates automatically from due date"}
          onClick={(e) => e.stopPropagation()}
        >
          {saving ? "…" : status}
        </button>
      </PopoverTrigger>
      {menuOptions.length ? (
        <PopoverContent
          align="end"
          sideOffset={6}
          className="z-[60] w-auto min-w-[12rem] neo-panel p-2 text-[var(--t-text)] t-glass"
          data-testid={`invoice-status-menu-${row.invoiceNumber}`}
        >
          <div className="flex flex-col gap-1">
            {menuOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className="rounded-[var(--t-event-pill-radius)] px-3 py-2 text-left text-[10px] font-normal uppercase tracking-[0.16em] transition-colors hover:bg-black/[0.04]"
                onClick={() => selectStatus(opt.value)}
                data-testid={`invoice-status-option-${opt.value}-${row.invoiceNumber}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </PopoverContent>
      ) : null}
    </Popover>
  );
}
